/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { createContext, useContext, useMemo, useState } from 'react'

import useLocalStorage from '@/hooks/useLocalStorage'
import useMediaQuery from '@/hooks/useMediaQuery'

export type NavMode = 'pinned' | 'hamburger'

/** Tailwind's `lg`. Narrower than this the nav is always a hamburger, whatever the user chose. */
export const PINNABLE_QUERY = '(min-width: 1024px)'

interface NavModeValue {
  /** The user's choice, kept even while a narrow window overrides it. */
  mode: NavMode
  setMode: (mode: NavMode) => void
  /** The window is wide enough to pin the nav. */
  canPin: boolean
  /**
   * The nav is down to the floating menu button, so pages show their own title bar (with
   * room at the left for that button) in place of the pinned nav's context.
   */
  isCollapsed: boolean
  /** The collapsed nav's drawer is open. Pages' titles open it as well as the menu button. */
  drawerOpen: boolean
  setDrawerOpen: (open: boolean) => void
}

// Outside the app shell (tests, standalone pages) behave as if the nav were pinned.
const NavModeContext = createContext<NavModeValue>({
  mode: 'pinned',
  setMode: () => undefined,
  canPin: true,
  isCollapsed: false,
  drawerOpen: false,
  setDrawerOpen: () => undefined,
})

export const NavModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Kept in this browser only, not in the user's profile.
  const [mode, setMode] = useLocalStorage<NavMode>('navMode', 'pinned')
  const canPin = useMediaQuery(PINNABLE_QUERY)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const value = useMemo(
    () => ({ mode, setMode, canPin, isCollapsed: mode === 'hamburger' || !canPin, drawerOpen, setDrawerOpen }),
    // useLocalStorage makes a new setMode each render, but an old one still writes the value
    // it's given, so it needn't refresh the context.
    [mode, canPin, drawerOpen]
  )
  return <NavModeContext.Provider value={value}>{children}</NavModeContext.Provider>
}

export const useNavMode = (): NavModeValue => useContext(NavModeContext)
